import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { compareDateOnlyDesc, formatDateOnly } from "../../utils/date";
import "./Blog.css";

import { SITE_URL, blogCoverAttributes } from "../../utils/siteSeo.mjs";

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/content/blog/posts.json", {
          cache: "no-store",
        });
        if (!res.ok)
          throw new Error(`Failed to fetch posts.json: ${res.status}`);
        const data = await res.json();
        data.sort((a, b) => compareDateOnlyDesc(a.date, b.date));
        setPosts(data);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="blog-page">
        <p>Loading…</p>
      </div>
    );
  }

  if (err) {
    return (
      <div className="blog-page">
        <p className="has-text-danger">{err}</p>
      </div>
    );
  }

  const [featuredPost, ...supportingPosts] = posts;

  return (
    <div className="blog-page">
      <Helmet>
        <title>ArmorySync Blog | Firearm App Updates & Training Insights</title>
        <meta
          name="description"
          content="Read ArmorySync updates about Range Pocket, competition scoring, match stories, firearm records, and training workflows."
        />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <meta property="og:title" content="ArmorySync Blog | Firearm App Updates & Training Insights" />
        <meta
          property="og:description"
          content="Match stories, product updates, and practical guidance for competition scoring, firearm records, and training."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta property="og:image" content={`${SITE_URL}/features/feature-armory-status.webp`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "ArmorySync Blog",
            url: `${SITE_URL}/blog`,
            description:
              "Match stories, product updates, and practical guidance for competition scoring, firearm records, and training.",
            publisher: {
              "@type": "Organization",
              name: "ArmorySync",
              url: SITE_URL,
            },
          })}
        </script>
        {posts.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              itemListElement: posts.map((post, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `${SITE_URL}/blog/${post.slug}`,
                name: post.title,
              })),
            })}
          </script>
        )}
      </Helmet>

      <header className="blog-hero">
        <div>
          <div className="blog-kicker">ArmorySync Blog</div>
          <h1 className="blog-title">From the range to match day.</h1>
        </div>
        <p className="blog-tagline">
          Match stories, product updates, and practical guides for competition
          scoring, firearm records, and personal training.
        </p>
        <nav className="blog-topic-links" aria-label="Blog topics">
          <Link to="/blog/range-pocket-records">How records work</Link>
          <Link to="/features">Range Pocket features</Link>
          <Link to="/range-log-app">Range logging</Link>
          <Link to="/blog/range-time-to-training-habit">Training habits</Link>
          <Link to="/firearm-inventory-app">Inventory app</Link>
          <Link to="/range-pocket">Range Pocket</Link>
        </nav>
      </header>

      {featuredPost && (
        <section className="blog-featured" aria-label="Featured article">
          {featuredPost.cover && (
            <Link
              className="blog-featured-media"
              to={`/blog/${featuredPost.slug}`}
              aria-label={`Read ${featuredPost.title}`}
            >
              <img {...blogCoverAttributes(featuredPost, "(max-width: 760px) calc(100vw - 32px), 620px")} alt={featuredPost.coverAlt || featuredPost.title} loading="eager" />
            </Link>
          )}
          <div className="blog-featured-copy">
            <p className="blog-card-meta">
              {formatDateOnly(featuredPost.date)}
            </p>
            <h2>
              <Link to={`/blog/${featuredPost.slug}`}>{featuredPost.title}</Link>
            </h2>
            <p>{featuredPost.subtitle}</p>
            <Link className="blog-read-link" to={`/blog/${featuredPost.slug}`}>
              Read article
            </Link>
          </div>
        </section>
      )}

      <section className="blog-grid" aria-label="More blog posts">
        {supportingPosts.map((p) => (
          <article key={p.slug} className="blog-card">
            {p.cover && (
              <div className="blog-card-media">
                <Link to={`/blog/${p.slug}`} aria-label={`Read ${p.title}`}>
                  <figure>
                    <img {...blogCoverAttributes(p, "(max-width: 760px) calc(100vw - 32px), 380px")} alt={p.title} loading="lazy" />
                  </figure>
                </Link>
              </div>
            )}

            <div className="blog-card-body">
              <h2 className="blog-card-title">
                <Link to={`/blog/${p.slug}`}>{p.title}</Link>
              </h2>
              <p className="blog-card-meta">
                {formatDateOnly(p.date)}
              </p>
              <p className="blog-card-subtitle">{p.subtitle}</p>

              {p.tags?.length > 0 && (
                <div className="blog-tags" style={{ marginTop: "10px" }}>
                  {p.tags.map((t) => (
                    <span className="blog-tag" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

export const BlogList_Page = {
  path: "/blog",
  element: <BlogList />,
};
